import { FormProps } from '@alphabait/ui/form'
import { RadioGroupWithDescriptionInput } from './custom-types'
import { useEffect, useState } from 'react'
import cx from 'classnames'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styles from './form.module.css'

type RadioGroupWithDescriptionType = {
  name: string
  label: string
  description?: string
}

// Legacy mode props
type LegacyProps = {
  field: RadioGroupWithDescriptionInput
  formProps: FormProps
}

// Direct mode props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  options: RadioGroupWithDescriptionType[]
  value?: string
  disabled?: boolean
  testId?: string
}

type RadioGroupWithDescriptionProps = LegacyProps | DirectProps

function isLegacyProps(
  props: RadioGroupWithDescriptionProps,
): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

export default function RadioGroupWithDescription(
  props: RadioGroupWithDescriptionProps,
) {
  const { t } = useTranslation()

  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const options = isLegacyProps(props) ? props.field.options : props.options
  const initialValue = isLegacyProps(props) ? props.field.value : props.value
  const disabled = isLegacyProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const testId = isLegacyProps(props) ? props.field.uiOptions?.testId : props.testId

  const [isSelected, setIsSelected] = useState<string>()
  useEffect(() => {
    setIsSelected(initialValue)
  }, [initialValue])

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  return (
    <div>
      <div className="-space-y-px rounded-md">
      {options &&
        options.map((category: RadioGroupWithDescriptionType, index) => {
          return (
            <label
              key={category.name}
              htmlFor={category.name}
              onClick={() => setIsSelected(category.name)}
              className={cx(
                index === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                index === options.length - 1
                  ? 'rounded-bl-md rounded-br-md'
                  : '',
                isSelected === category.name
                  ? 'border-marine-30 bg-gray-800'
                  : 'border-ui',
                'relative flex cursor-pointer border p-4 focus:outline-none',
                disabled && 'pointer-events-none',
              )}
            >
              <div className="w-6">
                <input
                  type="radio"
                  value={category.name}
                  checked={value === category.name}
                  onChange={(e) => {
                    onChange(e.target.value)
                    setIsSelected(e.target.value)
                  }}
                  className="w-6 h-6"
                  id={category.name}
                  data-testid={`${testId}-${category.name}`}
                  disabled={disabled}
                />
              </div>
              <div className="mx-2 align-top grid">
                <p
                  className={
                    isSelected === category.name
                      ? 'self-stretch text-sm font-bold text-black-1'
                      : 'text-sm leading-5 font-medium'
                  }
                >
                  {category.label}
                </p>
                {category.description && (
                  <p className="self-stretch text-gray-300 text-sm leading-[1.1875rem]">
                    {category.description}
                  </p>
                )}
              </div>
            </label>
          )
        })}
      </div>
      {error && (
        <span
          className={styles.errorMessage}
          data-testid={`${fieldName}-error`}
        >
          {t(`validation_messages.${error.message}`)}
        </span>
      )}
    </div>
  )
}
